import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  getMyBeatAccount,
  getMyBeatSaves,
  removeBeatSave,
  requestLicensedFiles,
  type SavedBeatItem,
} from "@/lib/bkls.functions";
import { formatMoney, UNIVERSAL_RESTRICTIONS, type IssuedLicense } from "@/lib/bkls";
import { BeatCard } from "@/components/beats/BeatCard";
import { beatsCatalogQuery, beatsTiersQuery } from "@/lib/bkls-queries";
import { relatedBeats, type RelatedBeat } from "@/lib/bkls-marketplace";

export const Route = createFileRoute("/_authenticated/beats/account")({
  head: () => ({
    meta: [
      { title: "Your Beat Licences · Buddha KA" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BeatsAccount,
});

function BeatsAccount() {
  const fetchAccount = useServerFn(getMyBeatAccount);
  const fetchFiles = useServerFn(requestLicensedFiles);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<IssuedLicense | null>(null);
  const [files, setFiles] = useState<Record<string, { label: string; url: string }[]>>({});
  const [error, setError] = useState<string | null>(null);

  const account = useQuery({
    queryKey: ["bkls", "account"],
    queryFn: () => fetchAccount({ data: {} } as never),
  });

  async function download(licenseId: string) {
    setError(null);
    const result = await fetchFiles({ data: { licenseId } });
    if ("error" in result) {
      setError(result.error);
      return;
    }
    setFiles((prev) => ({ ...prev, [licenseId]: result.files }));
    void queryClient.invalidateQueries({ queryKey: ["bkls", "account"] });
  }

  const licenses = account.data?.licenses ?? [];
  const requests = account.data?.requests ?? [];

  return (
    <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20">
      <header className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Your account</p>
          <h1 className="mt-3 font-serif text-4xl">Beat licences.</h1>
        </div>
        <Link to="/beats/catalog" className="text-sm text-gold hover:text-gold-luminous">
          Browse the catalog →
        </Link>
      </header>

      {error && (
        <p role="alert" className="mt-6 text-sm text-red-400">
          {error}
        </p>
      )}

      <section className="mt-12" aria-labelledby="active-licenses">
        <h2 id="active-licenses" className="font-serif text-2xl text-gold-luminous">
          Active licences
        </h2>
        {account.isLoading ? (
          <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
        ) : licenses.length === 0 ? (
          <p className="mt-4 rounded-2xl border hairline bg-charcoal p-6 text-sm text-muted-foreground">
            You have no beat licences yet. Licences appear here the moment one is issued to your account.
          </p>
        ) : (
          <ul className="mt-4 space-y-3">
            {licenses.map((l) => (
              <li key={l.id} className="rounded-2xl border hairline bg-charcoal p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div>
                    <p className="font-serif text-xl text-ivory">{l.beat_title}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {l.tier_name} · {l.license_number} · issued{" "}
                      {new Date(l.issued_at).toISOString().slice(0, 10)}
                    </p>
                  </div>
                  <p className="text-sm text-gold">{formatMoney(l.price_cents, l.currency)}</p>
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setOpen(l)}
                    className="rounded-full border hairline px-4 py-2 text-xs text-foreground/80 hover:border-gold hover:text-gold"
                  >
                    View licence document
                  </button>
                  <button
                    type="button"
                    onClick={() => download(l.id)}
                    className="rounded-full border border-gold px-4 py-2 text-xs text-gold-luminous transition-all hover:bg-gold hover:text-obsidian"
                  >
                    Get download links
                  </button>
                </div>
                {files[l.id]?.length ? (
                  <ul className="mt-4 space-y-1 text-sm">
                    {files[l.id].map((f) => (
                      <li key={f.url}>
                        <a href={f.url} className="text-gold hover:text-gold-luminous">
                          {f.label}
                        </a>{" "}
                        <span className="text-xs text-muted-foreground">(link expires in 10 minutes)</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-14" aria-labelledby="requests">
        <h2 id="requests" className="font-serif text-2xl text-gold-luminous">
          Custom requests
        </h2>
        {requests.length === 0 ? (
          <p className="mt-4 rounded-2xl border hairline bg-charcoal p-6 text-sm text-muted-foreground">
            No custom licensing requests yet.{" "}
            <Link to="/beats/custom" className="text-gold hover:text-gold-luminous">
              Send one
            </Link>
            .
          </p>
        ) : (
          <ul className="mt-4 space-y-3">
            {requests.map((r: any) => (
              <li key={r.id} className="rounded-2xl border hairline bg-charcoal p-5 text-sm">
                <div className="flex flex-wrap justify-between gap-3">
                  <span className="text-ivory">{r.beat_slug ?? "Unspecified beat"}</span>
                  <span className="text-xs uppercase tracking-[0.2em] text-gold">{r.status}</span>
                </div>
                <p className="mt-2 text-muted-foreground">{r.intended_use}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <SavedBeats />

      <RecommendedBeats />



      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Licence ${open.license_number}`}
          className="fixed inset-0 z-[100] overflow-y-auto bg-obsidian/95 p-4 sm:p-10"
        >
          <div className="mx-auto max-w-3xl rounded-2xl border hairline bg-charcoal p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-serif text-2xl text-gold-luminous">{open.beat_title}</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {open.tier_name} · {open.license_number}
                  {open.template_version ? ` · template v${open.template_version}` : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(null)}
                className="rounded-full border hairline px-3 py-1.5 text-xs text-foreground/80 hover:border-gold hover:text-gold"
              >
                Close
              </button>
            </div>

            <dl className="mt-6 grid gap-3 text-sm sm:grid-cols-2">
              {Object.entries(open.terms_snapshot ?? {})
                .filter(([k, v]) => !Array.isArray(v) && typeof v !== "object" && k !== "snapshot_at")
                .map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[10px] uppercase tracking-[0.2em] text-gold/80">{k.replace(/_/g, " ")}</dt>
                    <dd className="mt-1 text-muted-foreground">{String(v)}</dd>
                  </div>
                ))}
            </dl>

            <h3 className="mt-8 font-serif text-lg text-ivory">Restrictions</h3>
            <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
              {[
                ...((open.terms_snapshot?.restrictions as string[]) ?? []),
                ...UNIVERSAL_RESTRICTIONS,
              ].map((r) => (
                <li key={r} className="pl-4 -indent-4">
                  — {r}
                </li>
              ))}
            </ul>

            {open.agreement_md && (
              <>
                <h3 className="mt-8 font-serif text-lg text-ivory">Agreement</h3>
                <pre className="mt-3 whitespace-pre-wrap font-sans text-sm leading-relaxed text-muted-foreground">
                  {open.agreement_md}
                </pre>
              </>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => window.print()}
                className="rounded-full border hairline px-4 py-2 text-xs text-foreground/80 hover:border-gold hover:text-gold"
              >
                Print / save as PDF
              </button>
              <a
                href={`data:text/plain;charset=utf-8,${encodeURIComponent(
                  `${open.license_number}\n${open.beat_title} — ${open.tier_name}\n\n${JSON.stringify(
                    open.terms_snapshot,
                    null,
                    2,
                  )}\n\n${open.agreement_md ?? ""}`,
                )}`}
                download={`${open.license_number}.txt`}
                className="rounded-full border hairline px-4 py-2 text-xs text-foreground/80 hover:border-gold hover:text-gold"
              >
                Download licence record
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Readable fallback label for a save whose beat is no longer public.
 * The slug is something the member already saw when they saved it, so this
 * is not new information — it just avoids showing a raw hyphenated token.
 */
function humanizeSlug(slug: string): string {
  const words = slug.replace(/[-_]+/g, " ").trim();
  if (!words) return "Saved beat";
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function SavedList({

  heading,
  id,
  kind,
  items,
  emptyText,
  onRemove,
  pending,
}: {
  heading: string;
  id: string;
  kind: "beat" | "beat_wishlist";
  items: SavedBeatItem[];
  emptyText: string;
  onRemove: (item: SavedBeatItem) => void;
  pending: string | null;
}) {
  return (
    <section className="mt-14" aria-labelledby={id}>
      <h2 id={id} className="font-serif text-2xl text-gold-luminous">
        {heading}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Saved items are reminders only — they are not purchases, ownership or issued licences.
      </p>
      {items.length === 0 ? (
        <p className="mt-4 rounded-2xl border hairline bg-charcoal p-6 text-sm text-muted-foreground">
          {emptyText}{" "}
          <Link to="/beats/catalog" className="text-gold hover:text-gold-luminous">
            Browse the catalog
          </Link>
          .
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {items.map((item) => {
            const key = `${kind}:${item.slug}`;
            const isPending = pending === key;
            // Only a beat that is live in the public catalog is ever linked.
            // Anything else renders as inert text — we deliberately do not
            // say *why* it is unavailable, since draft/archived/retired is
            // internal editorial state.
            const label = item.available ? (item.title ?? item.slug) : humanizeSlug(item.slug);
            return (
              <li
                key={key}
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border hairline bg-charcoal p-5"
              >
                <div className="min-w-0">
                  {item.available ? (
                    <Link
                      to="/beats/$slug"
                      params={{ slug: item.slug }}
                      className="font-serif text-lg text-gold hover:text-gold-luminous"
                    >
                      {label}
                    </Link>
                  ) : (
                    <p className="font-serif text-lg text-muted-foreground">
                      {label}
                      <span className="ml-2 align-middle rounded-full border hairline px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                        Unavailable
                      </span>
                    </p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    {item.available
                      ? "Available in the public catalog"
                      : "Not currently available. You can remove it from your list."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(item)}
                  disabled={isPending}
                  aria-label={`Remove ${label} from your ${kind === "beat" ? "favourites" : "wishlist"}`}
                  className="min-h-11 rounded-full border hairline px-4 py-2 text-xs text-foreground/80 hover:border-gold hover:text-gold disabled:opacity-60"
                >
                  {isPending ? "Removing…" : "Remove"}
                </button>
              </li>
            );
          })}
        </ul>
      )}

    </section>
  );
}

function SavedBeats() {
  const fetchSaves = useServerFn(getMyBeatSaves);
  const remove = useServerFn(removeBeatSave);
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("");

  const saves = useQuery({
    queryKey: ["bkls", "saves"],
    queryFn: () => fetchSaves({ data: {} } as never),
  });

  async function onRemove(item: SavedBeatItem) {
    const key = `${item.kind}:${item.slug}`;
    setPending(key);
    try {
      await remove({ data: { slug: item.slug, kind: item.kind } });
      setStatus(
        `${item.title ?? item.slug} removed from your ${item.kind === "beat" ? "favourites" : "wishlist"}.`,
      );
      await queryClient.invalidateQueries({ queryKey: ["bkls", "saves"] });
    } catch {
      setStatus("Could not remove that item. Please try again.");
    } finally {
      setPending(null);
    }
  }

  return (
    <>
      <p aria-live="polite" className="sr-only">
        {status}
      </p>
      {saves.isLoading ? (
        <p className="mt-14 text-sm text-muted-foreground">Loading your saved beats…</p>
      ) : (
        <>
          <SavedList
            heading="Wishlist"
            id="beat-wishlist"
            kind="beat_wishlist"
            items={saves.data?.wishlist ?? []}
            emptyText="Your wishlist is empty."
            onRemove={onRemove}
            pending={pending}
          />
          <SavedList
            heading="Favourites"
            id="beat-favourites"
            kind="beat"
            items={saves.data?.favorites ?? []}
            emptyText="You have not favourited any beats yet."
            onRemove={onRemove}
            pending={pending}
          />
        </>
      )}
    </>
  );
}


/**
 * Recommendations derived only from what this member already engaged with:
 * their own licences and saves, matched against the public catalog on shared
 * collection, genre, mood, tempo and key. No cross-member behaviour is used.
 */
function RecommendedBeats() {
  const fetchAccount = useServerFn(getMyBeatAccount);
  const fetchSaves = useServerFn(getMyBeatSaves);
  const catalog = useQuery(beatsCatalogQuery);
  const tiers = useQuery(beatsTiersQuery);

  const account = useQuery({
    queryKey: ["bkls", "account"],
    queryFn: () => fetchAccount({ data: {} } as never),
  });
  const saves = useQuery({
    queryKey: ["bkls", "saves"],
    queryFn: () => fetchSaves({ data: {} } as never),
  });

  const rows = catalog.data ?? [];
  const ownedSlugs = new Set((account.data?.licenses ?? []).map((l) => l.beat_slug));
  const seedSlugs = new Set<string>([
    ...ownedSlugs,
    ...[...(saves.data?.favorites ?? []), ...(saves.data?.wishlist ?? [])]
      .filter((s) => s.available)
      .map((s) => s.slug),
  ]);

  const scored = new Map<string, RelatedBeat>();
  for (const slug of seedSlugs) {
    const seed = rows.find((b) => b.slug === slug);
    if (!seed) continue;
    for (const rel of relatedBeats(seed, rows, 6)) {
      if (seedSlugs.has(rel.beat.slug)) continue;
      const existing = scored.get(rel.beat.slug);
      if (!existing || rel.score > existing.score) scored.set(rel.beat.slug, rel);
    }
  }
  const recommendations = Array.from(scored.values())
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);

  if (catalog.isLoading || recommendations.length === 0) return null;

  return (
    <section className="mt-14" aria-labelledby="recommended-beats">
      <h2 id="recommended-beats" className="font-serif text-2xl text-gold-luminous">
        Close to what you already hold
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Matched against your licences and saves on shared collection, genre, mood, tempo and key.
      </p>
      <ul className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {recommendations.map((r) => (
          <li key={r.beat.id}>
            <BeatCard beat={r.beat} tiers={tiers.data ?? []} reasons={r.reasons} />
          </li>
        ))}
      </ul>
    </section>
  );
}
